import {
  ArrowRight,
  BadgeDollarSign,
  Bike,
  Boxes,
  ChefHat,
  ChevronDown,
  CircleAlert,
  CircleCheck,
  CircleDollarSign,
  ClipboardCheck,
  Clock3,
  CreditCard,
  ExternalLink,
  Eye,
  FileBarChart,
  HandCoins,
  Headphones,
  LayoutDashboard,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  MapPinned,
  Menu,
  MessageCircleMore,
  PackageCheck,
  PhoneCall,
  Plus,
  Printer,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  ShoppingBag,
  Store,
  Truck,
  UserRound,
  UsersRound,
  UtensilsCrossed,
  WalletCards,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, patch, post } from "./api";
import Modal from "./components/Modal";
import Toast, { type ToastData } from "./components/Toast";
import { ProductDialog } from "./Storefront";
import { dateTime, money, paymentLabel, phoneMask, statusLabel } from "./lib";
import type {
  Address,
  CartItem,
  Customer,
  OrderStatus,
  OrderSummary,
  Product,
  StaffUser,
  StoreConfig,
} from "./types";

type Section =
  | "dashboard"
  | "orders"
  | "counter"
  | "kitchen"
  | "dispatch"
  | "couriers"
  | "finance"
  | "customers"
  | "menu"
  | "whatsapp"
  | "settings";

interface OperationsSummary {
  orders: number;
  grossSales: number;
  averageTicket: number;
  statuses: Record<string, number>;
  payments: Array<{ method: string; count: number; total: number }>;
  couriers: Array<{
    id: string;
    name: string;
    phone: string;
    vehicle: string;
    deliveryCount: number;
    deliveryFees: number;
  }>;
}

interface Courier {
  id: string;
  name: string;
  phone: string;
  vehicle: string;
  plate: string;
  fixedDailyRate: number;
  active: boolean;
}

interface CustomerRow {
  id: string;
  name: string;
  phone: string;
  email?: string;
  createdAt: string;
  ordersCount: number;
  lifetimeValue: number;
}

interface ClosingState {
  courierShifts: Array<{
    id: string;
    courierId: string;
    courierName: string;
    closedAt: string;
    deliveryCount: number;
    deliveryFees: number;
    extras: number;
    advances: number;
    amountDue: number;
    notes?: string | null;
  }>;
  cashSession: {
    id: string;
    closedAt: string;
    openingAmount: number;
    expectedAmount: number;
    countedAmount: number;
    differenceAmount: number;
    notes?: string | null;
  } | null;
}

const navItems: Array<{
  id: Section;
  label: string;
  icon: typeof LayoutDashboard;
  group?: string;
}> = [
  { id: "dashboard", label: "Visão geral", icon: LayoutDashboard, group: "Hoje" },
  { id: "orders", label: "Pedidos", icon: ShoppingBag },
  { id: "counter", label: "Balcão & telefone", icon: Store },
  { id: "kitchen", label: "Cozinha", icon: ChefHat, group: "Operação" },
  { id: "dispatch", label: "Expedição", icon: MapPinned },
  { id: "couriers", label: "Motoboys", icon: Bike },
  { id: "finance", label: "Fechamentos", icon: CircleDollarSign, group: "Gestão" },
  { id: "customers", label: "Clientes", icon: UsersRound },
  { id: "menu", label: "Cardápio", icon: UtensilsCrossed },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircleMore, group: "Canais" },
  { id: "settings", label: "Configurações", icon: Settings },
];

const boardColumns: Array<{ status: OrderStatus; title: string; hint: string }> = [
  { status: "awaiting_payment", title: "Aguardando Pix", hint: "Confirmar antes de produzir" },
  { status: "received", title: "Recebidos", hint: "Fila de entrada" },
  { status: "preparing", title: "Em preparo", hint: "Na cozinha agora" },
  { status: "ready", title: "Prontos", hint: "Retirada ou expedição" },
  { status: "out_for_delivery", title: "Em entrega", hint: "Com o motoboy" },
];

const nextStatus: Partial<Record<OrderStatus, OrderStatus>> = {
  received: "preparing",
  preparing: "ready",
  ready: "out_for_delivery",
  out_for_delivery: "delivered",
};

const nextLabel: Partial<Record<OrderStatus, string>> = {
  received: "Iniciar preparo",
  preparing: "Marcar pronto",
  ready: "Saiu para entrega",
  out_for_delivery: "Marcar entregue",
};

function StaffLogin({ onLogin }: { onLogin: (staff: StaffUser) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await post<{ staff: StaffUser }>("staff/login", { email, password });
      onLogin(response.staff);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Falha ao entrar.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-login">
      <div className="admin-login-visual">
        <div className="admin-login-brand">
          <img src="/assets/maxhouse-logo-premium.png" alt="Max House" />
          <span><strong>MAX HOUSE</strong><small>OPERAÇÃO 360°</small></span>
        </div>
        <div className="admin-login-copy">
          <span className="eyebrow">Tudo no mesmo ritmo</span>
          <h1>Da primeira ligação à última entrega.</h1>
          <p>Pedidos, cozinha, expedição, motoboys e financeiro em uma única operação.</p>
        </div>
        <div className="admin-login-flow">
          <span><PhoneCall size={20} /> Atendimento</span>
          <ArrowRight size={16} />
          <span><ChefHat size={20} /> Cozinha</span>
          <ArrowRight size={16} />
          <span><Bike size={20} /> Entrega</span>
        </div>
      </div>
      <div className="admin-login-panel">
        <form onSubmit={submit}>
          <span className="login-lock"><LockKeyhole size={24} /></span>
          <span className="eyebrow">Portal protegido</span>
          <h2>Bem-vindo à operação</h2>
          <p>Use seu acesso individual para entrar.</p>
          <label>
            E-mail
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="username" required />
          </label>
          <label>
            Senha
            <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required />
          </label>
          {error && <div className="inline-error">{error}</div>}
          <button className="primary-button" disabled={busy}>
            {busy ? <LoaderCircle className="spin" size={18} /> : <ShieldCheck size={18} />}
            {busy ? "Entrando..." : "Entrar no portal"}
          </button>
          <a href="/">← Voltar para a loja</a>
        </form>
      </div>
    </div>
  );
}

function ChangePasswordDialog({
  open,
  onChanged,
}: {
  open: boolean;
  onChanged: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (password !== confirm) {
      setError("As senhas não coincidem.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await post("staff/change-password", { password });
      onChanged();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Falha ao trocar a senha.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open={open} onClose={() => undefined} title="Proteja seu acesso" eyebrow="Primeiro acesso">
      <form className="stack-form" onSubmit={submit}>
        <div className="info-box">
          <ShieldCheck size={18} />
          A senha temporária funcionou. Crie agora uma senha exclusiva da Max House.
        </div>
        <label>
          Nova senha
          <input type="password" minLength={10} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" required />
        </label>
        <label>
          Confirme a nova senha
          <input type="password" minLength={10} value={confirm} onChange={(event) => setConfirm(event.target.value)} autoComplete="new-password" required />
        </label>
        <small>Use no mínimo 10 caracteres, com maiúsculas, minúsculas e números.</small>
        {error && <div className="inline-error">{error}</div>}
        <button className="primary-button" disabled={busy}>{busy ? "Salvando..." : "Salvar e entrar"}</button>
      </form>
    </Modal>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  foot,
  tone,
}: {
  icon: typeof LayoutDashboard;
  label: string;
  value: string;
  foot: string;
  tone: string;
}) {
  return (
    <article className={`stat-card stat-${tone}`}>
      <span className="stat-icon"><Icon size={22} /></span>
      <div><small>{label}</small><strong>{value}</strong><span>{foot}</span></div>
    </article>
  );
}

function OrderCard({
  order,
  onAction,
  compact = false,
  couriers = [],
}: {
  order: OrderSummary;
  onAction: (order: OrderSummary, action: string, value?: string) => void;
  compact?: boolean;
  couriers?: Courier[];
}) {
  const [details, setDetails] = useState(false);
  return (
    <article className={`operation-order ${compact ? "compact" : ""}`}>
      <header>
        <span className="order-number">#{order.number}</span>
        <span className={`source-badge source-${order.source}`}>{order.source}</span>
        <time>{dateTime(order.createdAt)}</time>
      </header>
      <div className="order-customer-row">
        <span><strong>{order.customerName}</strong><small>{phoneMask(order.customerPhone)}</small></span>
        <strong>{money(order.total)}</strong>
      </div>
      <div className="order-items-short">
        {order.items.map((item) => (
          <span key={item.id}><b>{item.quantity}×</b> {item.name}</span>
        ))}
      </div>
      {details && (
        <div className="order-details">
          {order.items.map((item) => (
            <div key={item.id}>
              <strong>{item.quantity}× {item.name}</strong>
              {item.options.map((option) => <small key={`${item.id}-${option.optionId}`}>+ {option.optionName}</small>)}
              {item.notes && <em>Obs.: {item.notes}</em>}
            </div>
          ))}
          {order.notes && <p><b>Pedido:</b> {order.notes}</p>}
          {order.address && (
            <p><b>Entrega:</b> {order.address.street}, {order.address.number} · {order.address.neighborhood}</p>
          )}
        </div>
      )}
      <button className="details-toggle" onClick={() => setDetails((value) => !value)}>
        <Eye size={15} /> {details ? "Ocultar detalhes" : "Ver detalhes"}
      </button>
      {order.status === "awaiting_payment" && (
        <button className="order-action action-pix" onClick={() => onAction(order, "confirm_pix")}>
          <CircleCheck size={17} /> Confirmar Pix
        </button>
      )}
      {order.status === "ready" && order.fulfillmentType === "delivery" && (
        <label className="courier-select">
          <Bike size={16} />
          <select value={order.courierId || ""} onChange={(event) => onAction(order, "assign_courier", event.target.value)}>
            <option value="">Selecionar motoboy</option>
            {couriers.filter((courier) => courier.active).map((courier) => <option key={courier.id} value={courier.id}>{courier.name}</option>)}
          </select>
          <ChevronDown size={15} />
        </label>
      )}
      {nextStatus[order.status] && (order.status !== "ready" || order.fulfillmentType !== "delivery" || order.courierId) && (
        <button className="order-action" onClick={() => onAction(order, "status", nextStatus[order.status])}>
          {nextLabel[order.status]} <ArrowRight size={16} />
        </button>
      )}
    </article>
  );
}

export default function AdminPortal() {
  const [staff, setStaff] = useState<StaffUser | null>(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [section, setSection] = useState<Section>("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [store, setStore] = useState<StoreConfig | null>(null);
  const [summary, setSummary] = useState<OperationsSummary | null>(null);
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [couriers, setCouriers] = useState<Courier[]>([]);
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [closings, setClosings] = useState<ClosingState>({
    courierShifts: [],
    cashSession: null,
  });
  const [busy, setBusy] = useState(false);
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [toast, setToast] = useState<ToastData | null>(null);
  const toastCounter = useRef(0);

  const showToast = useCallback((message: string, tone: "success" | "error" = "success") => {
    toastCounter.current += 1;
    setToast({ id: toastCounter.current, message, tone });
  }, []);

  useEffect(() => {
    Promise.allSettled([
      api<{ staff: StaffUser }>("staff/me"),
      api<{ store: StoreConfig }>("public/config"),
    ]).then(([staffResult, configResult]) => {
      if (staffResult.status === "fulfilled") setStaff(staffResult.value.staff);
      if (configResult.status === "fulfilled") setStore(configResult.value.store);
      setCheckingSession(false);
    });
  }, []);

  const refresh = useCallback(async (quiet = false) => {
    if (!staff) return;
    if (!quiet) setBusy(true);
    try {
      const [summaryResponse, ordersResponse, courierResponse, closingResponse] = await Promise.all([
        api<{ summary: OperationsSummary }>("operations/summary"),
        api<{ orders: OrderSummary[] }>("orders"),
        api<{ couriers: Courier[] }>("couriers"),
        api<{ closings: ClosingState }>("closings/today"),
      ]);
      setSummary(summaryResponse.summary);
      setOrders(ordersResponse.orders);
      setCouriers(courierResponse.couriers);
      setClosings(closingResponse.closings);
      setLastSync(new Date());
    } catch (error) {
      if (!quiet) showToast(error instanceof Error ? error.message : "Falha ao atualizar.", "error");
    } finally {
      setBusy(false);
    }
  }, [showToast, staff]);

  useEffect(() => {
    if (!staff) return;
    refresh();
    const interval = window.setInterval(() => refresh(true), 8000);
    return () => window.clearInterval(interval);
  }, [refresh, staff]);

  useEffect(() => {
    if (!["customers", "counter"].includes(section) || !staff || customers.length) return;
    api<{ customers: CustomerRow[] }>("customers")
      .then((response) => setCustomers(response.customers))
      .catch((error) => showToast(error instanceof Error ? error.message : "Falha ao carregar clientes.", "error"));
  }, [section, staff, customers.length, showToast]);

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(null), 3600);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  const orderAction = async (order: OrderSummary, action: string, value?: string) => {
    try {
      const body =
        action === "status"
          ? { action, status: value }
          : action === "assign_courier"
            ? { action, courierId: value }
            : { action };
      await post(`orders/${order.id}/action`, body);
      showToast(action === "confirm_pix" ? "Pix confirmado e pedido enviado à cozinha." : "Pedido atualizado.");
      await refresh(true);
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Falha ao atualizar pedido.", "error");
    }
  };

  const logout = async () => {
    await post("staff/logout");
    setStaff(null);
  };

  if (checkingSession) return <div className="splash-screen"><img src="/assets/maxhouse-logo-premium.png" alt="" /><div className="loading-line" /></div>;
  if (!staff) return <StaffLogin onLogin={setStaff} />;

  const activeNav = navItems.find((item) => item.id === section);
  const activeOrders = orders.filter((order) => !["delivered", "cancelled"].includes(order.status));

  return (
    <div className="admin-app">
      <aside className={`admin-sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="admin-brand">
          <img src="/assets/maxhouse-logo-premium.png" alt="" />
          <span><strong>MAX HOUSE</strong><small>OPERAÇÃO 360°</small></span>
          <button onClick={() => setSidebarOpen(false)}><X size={19} /></button>
        </div>
        <nav>
          {navItems.map((item, index) => {
            const previousGroup = index > 0 ? navItems[index - 1].group : undefined;
            const showGroup = item.group && item.group !== previousGroup;
            const Icon = item.icon;
            return (
              <div key={item.id}>
                {showGroup && <span className="nav-group">{item.group}</span>}
                <button
                  className={section === item.id ? "active" : ""}
                  onClick={() => { setSection(item.id); setSidebarOpen(false); }}
                >
                  <Icon size={19} /><span>{item.label}</span>
                  {item.id === "orders" && activeOrders.length > 0 && <b>{activeOrders.length}</b>}
                  {item.id === "kitchen" && summary?.statuses.received ? <b>{summary.statuses.received}</b> : null}
                </button>
              </div>
            );
          })}
        </nav>
        <div className="sidebar-user">
          <span className="user-avatar">{staff.name.split(" ").map((part) => part[0]).slice(0, 2).join("")}</span>
          <span><strong>{staff.name}</strong><small>{staff.role}</small></span>
          <button onClick={logout} title="Sair"><LogOut size={17} /></button>
        </div>
      </aside>
      <button className={`admin-sidebar-scrim ${sidebarOpen ? "show" : ""}`} onClick={() => setSidebarOpen(false)} aria-label="Fechar menu" />

      <main className="admin-main">
        <header className="admin-topbar">
          <button className="menu-toggle" onClick={() => setSidebarOpen(true)}><Menu size={21} /></button>
          <div>
            <span className="eyebrow">Max House · {new Intl.DateTimeFormat("pt-BR", { weekday: "long", day: "2-digit", month: "long" }).format(new Date())}</span>
            <h1>{activeNav?.label}</h1>
          </div>
          <div className="topbar-actions">
            <span className="live-indicator"><i /> Operação ao vivo</span>
            <button className="sync-button" onClick={() => refresh()} disabled={busy}>
              <RefreshCw size={17} className={busy ? "spin" : ""} />
              <span>{lastSync ? `Atualizado ${lastSync.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}` : "Atualizar"}</span>
            </button>
            <a href="/" target="_blank" rel="noreferrer"><ExternalLink size={17} /> <span>Ver loja</span></a>
          </div>
        </header>

        <div className="admin-content">
          {section === "dashboard" && (
            <Dashboard summary={summary} orders={orders} couriers={couriers} onAction={orderAction} />
          )}
          {section === "orders" && (
            <OrdersBoard orders={orders} couriers={couriers} onAction={orderAction} />
          )}
          {section === "counter" && store && (
            <CounterTerminal store={store} customers={customers} onDone={() => refresh(true)} showToast={showToast} />
          )}
          {section === "kitchen" && (
            <KitchenBoard orders={orders} onAction={orderAction} />
          )}
          {section === "dispatch" && (
            <DispatchBoard orders={orders} couriers={couriers} onAction={orderAction} />
          )}
          {section === "couriers" && (
            <CourierClose
              summary={summary}
              couriers={couriers}
              orders={orders}
              closings={closings}
              onDone={() => refresh(true)}
              showToast={showToast}
            />
          )}
          {section === "finance" && (
            <FinanceClose
              summary={summary}
              orders={orders}
              closings={closings}
              onDone={() => refresh(true)}
              showToast={showToast}
            />
          )}
          {section === "customers" && (
            <CustomersPanel customers={customers} />
          )}
          {section === "menu" && store && (
            <MenuManager store={store} showToast={showToast} />
          )}
          {section === "whatsapp" && (
            <WhatsappPanel store={store} />
          )}
          {section === "settings" && store && (
            <SettingsPanel store={store} />
          )}
        </div>
      </main>
      <ChangePasswordDialog
        open={Boolean(staff.mustChangePassword)}
        onChanged={() => setStaff((current) => current ? { ...current, mustChangePassword: false } : current)}
      />
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}

function Dashboard({
  summary,
  orders,
  couriers,
  onAction,
}: {
  summary: OperationsSummary | null;
  orders: OrderSummary[];
  couriers: Courier[];
  onAction: (order: OrderSummary, action: string, value?: string) => void;
}) {
  const pending = orders.filter((order) => !["delivered", "cancelled"].includes(order.status)).slice(0, 5);
  return (
    <>
      <section className="dashboard-welcome">
        <div><span className="eyebrow">Resumo da noite</span><h2>A casa está em movimento.</h2><p>Acompanhe os pontos que precisam de atenção agora.</p></div>
        <div className="welcome-pulse"><span><i /> Operação aberta</span><strong>{summary?.orders || 0} pedidos hoje</strong></div>
      </section>
      <div className="stats-grid">
        <StatCard icon={BadgeDollarSign} label="Vendas de hoje" value={money(summary?.grossSales || 0)} foot="Pedidos não cancelados" tone="red" />
        <StatCard icon={ShoppingBag} label="Pedidos" value={String(summary?.orders || 0)} foot={`${Object.values(summary?.statuses || {}).reduce((sum, value) => sum + value, 0)} movimentações`} tone="green" />
        <StatCard icon={CircleDollarSign} label="Ticket médio" value={money(summary?.averageTicket || 0)} foot="Média por pedido" tone="gold" />
        <StatCard icon={Clock3} label="Na operação" value={String(pending.length)} foot="Pedidos ativos agora" tone="blue" />
      </div>
      <div className="dashboard-grid">
        <section className="admin-card">
          <header className="admin-card-header"><div><span className="eyebrow">Fila em tempo real</span><h2>Pedidos que pedem atenção</h2></div><span className="count-chip">{pending.length}</span></header>
          <div className="dashboard-order-list">
            {pending.length ? pending.map((order) => <OrderCard key={order.id} order={order} couriers={couriers} onAction={onAction} compact />) : <EmptyState icon={CircleCheck} title="Tudo em dia" text="Nenhum pedido aguardando ação." />}
          </div>
        </section>
        <aside className="admin-card operations-radar">
          <header className="admin-card-header"><div><span className="eyebrow">Radar da operação</span><h2>Etapas</h2></div></header>
          {boardColumns.map((column) => (
            <div className="radar-row" key={column.status}>
              <span className={`radar-dot status-${column.status}`} />
              <span><strong>{column.title}</strong><small>{column.hint}</small></span>
              <b>{summary?.statuses[column.status] || 0}</b>
            </div>
          ))}
        </aside>
      </div>
    </>
  );
}

function OrdersBoard({
  orders,
  couriers,
  onAction,
}: {
  orders: OrderSummary[];
  couriers: Courier[];
  onAction: (order: OrderSummary, action: string, value?: string) => void;
}) {
  const [query, setQuery] = useState("");
  const filtered = orders.filter((order) =>
    `${order.number} ${order.customerName} ${order.customerPhone}`.toLowerCase().includes(query.toLowerCase()),
  );
  return (
    <>
      <div className="operation-toolbar">
        <div className="admin-search"><Search size={18} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Pedido, cliente ou telefone" /></div>
        <div className="toolbar-note"><Clock3 size={17} /> Atualização automática a cada 8 segundos</div>
      </div>
      <div className="kanban">
        {boardColumns.map((column) => {
          const columnOrders = filtered.filter((order) => order.status === column.status);
          return (
            <section className={`kanban-column column-${column.status}`} key={column.status}>
              <header><span><i /> {column.title}</span><b>{columnOrders.length}</b></header>
              <small>{column.hint}</small>
              <div className="kanban-list">
                {columnOrders.map((order) => <OrderCard key={order.id} order={order} couriers={couriers} onAction={onAction} />)}
                {!columnOrders.length && <div className="kanban-empty">Nenhum pedido</div>}
              </div>
            </section>
          );
        })}
      </div>
    </>
  );
}

function KitchenBoard({
  orders,
  onAction,
}: {
  orders: OrderSummary[];
  onAction: (order: OrderSummary, action: string, value?: string) => void;
}) {
  const kitchenOrders = orders.filter((order) => ["received", "preparing"].includes(order.status));
  return (
    <div className="kitchen-screen">
      <header className="kitchen-header">
        <div><span className="eyebrow">KDS · Kitchen Display</span><h2>Produção da cozinha</h2></div>
        <div><span><Clock3 size={18} /> {kitchenOrders.length} na fila</span><button onClick={() => window.print()}><Printer size={18} /> Imprimir tela</button></div>
      </header>
      <div className="kitchen-grid">
        {kitchenOrders.map((order) => {
          const minutes = Math.max(0, Math.floor((Date.now() - new Date(order.createdAt).getTime()) / 60000));
          return (
            <article className={`kitchen-ticket kitchen-${order.status}`} key={order.id}>
              <header><strong>#{order.number}</strong><span className={minutes >= 30 ? "late" : ""}>{minutes} min</span></header>
              <div className="kitchen-source">{order.fulfillmentType === "delivery" ? <Bike size={17} /> : <Store size={17} />}{order.fulfillmentType} · {order.customerName}</div>
              <div className="kitchen-items">
                {order.items.map((item) => (
                  <div key={item.id}>
                    <h3><b>{item.quantity}×</b>{item.name}</h3>
                    {item.options.map((option) => <span key={`${item.id}-${option.optionId}`}>+ {option.optionName}</span>)}
                    {item.notes && <em>OBS: {item.notes}</em>}
                  </div>
                ))}
              </div>
              {order.notes && <p className="kitchen-general-note">{order.notes}</p>}
              <footer>
                <button onClick={() => window.print()}><Printer size={17} /></button>
                <button className="primary-button" onClick={() => onAction(order, "status", nextStatus[order.status])}>{nextLabel[order.status]} <ArrowRight size={17} /></button>
              </footer>
            </article>
          );
        })}
        {!kitchenOrders.length && <EmptyState icon={ChefHat} title="Cozinha em dia" text="Os próximos pedidos aparecerão aqui automaticamente." />}
      </div>
    </div>
  );
}

function DispatchBoard({
  orders,
  couriers,
  onAction,
}: {
  orders: OrderSummary[];
  couriers: Courier[];
  onAction: (order: OrderSummary, action: string, value?: string) => void;
}) {
  const deliveryOrders = orders.filter((order) => order.fulfillmentType === "delivery" && ["ready", "out_for_delivery"].includes(order.status));
  return (
    <section className="dispatch-layout">
      <div className="admin-card">
        <header className="admin-card-header"><div><span className="eyebrow">Expedição</span><h2>Rotas prontas para envio</h2></div><span className="count-chip">{deliveryOrders.length}</span></header>
        <div className="dispatch-list">
          {deliveryOrders.map((order) => (
            <article key={order.id}>
              <header><span><strong>#{order.number}</strong><small>{order.customerName}</small></span><span className={`status-pill status-${order.status}`}>{statusLabel[order.status]}</span></header>
              <div className="dispatch-address">
                <MapPinned size={22} />
                <span><strong>{order.address?.street}, {order.address?.number}</strong><small>{order.address?.neighborhood} · {order.address?.reference}</small></span>
              </div>
              <div className="dispatch-contact"><PhoneCall size={17} /> {phoneMask(order.customerPhone)}</div>
              <div className="route-buttons">
                <a href={order.address?.mapsUrl} target="_blank" rel="noreferrer"><MapPinned size={17} /> Google Maps</a>
                <a href={order.address?.wazeUrl} target="_blank" rel="noreferrer"><Truck size={17} /> Waze</a>
              </div>
              {order.status === "ready" ? (
                <div className="dispatch-assignment">
                  <select value={order.courierId || ""} onChange={(event) => onAction(order, "assign_courier", event.target.value)}>
                    <option value="">Selecione o motoboy</option>
                    {couriers.filter((courier) => courier.active).map((courier) => <option key={courier.id} value={courier.id}>{courier.name} · {courier.vehicle}</option>)}
                  </select>
                  <button className="primary-button" disabled={!order.courierId} onClick={() => onAction(order, "status", "out_for_delivery")}>Enviar entrega <ArrowRight size={17} /></button>
                </div>
              ) : (
                <button className="primary-button" onClick={() => onAction(order, "status", "delivered")}><PackageCheck size={17} /> Confirmar entrega</button>
              )}
            </article>
          ))}
          {!deliveryOrders.length && <EmptyState icon={MapPinned} title="Nenhuma rota pendente" text="Pedidos prontos para entrega aparecerão aqui." />}
        </div>
      </div>
      <aside className="admin-card courier-availability">
        <header className="admin-card-header"><div><span className="eyebrow">Equipe de rua</span><h2>Motoboys ativos</h2></div></header>
        {couriers.filter((courier) => courier.active).map((courier) => (
          <div key={courier.id}><span className="user-avatar">{courier.name.split(" ").map((part) => part[0]).slice(0, 2).join("")}</span><span><strong>{courier.name}</strong><small>{courier.vehicle} · {courier.plate}</small></span><i /></div>
        ))}
      </aside>
    </section>
  );
}

function CounterTerminal({
  store,
  customers,
  onDone,
  showToast,
}: {
  store: StoreConfig;
  customers: CustomerRow[];
  onDone: () => void;
  showToast: (message: string, tone?: "success" | "error") => void;
}) {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [product, setProduct] = useState<Product | null>(null);
  const [categoryId, setCategoryId] = useState("all");
  const [source, setSource] = useState<"counter" | "phone" | "whatsapp">("counter");
  const [customerId, setCustomerId] = useState("");
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [addressId, setAddressId] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("counter");
  const [fulfillmentType, setFulfillmentType] = useState<"counter" | "pickup" | "delivery">("counter");
  const [busy, setBusy] = useState(false);
  const products = store.categories.flatMap((category) => category.products).filter((item) => categoryId === "all" || item.categoryId === categoryId);
  const total = cart.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);

  useEffect(() => {
    if (!customerId) {
      setAddresses([]);
      setAddressId("");
      return;
    }
    api<{ addresses: Address[] }>(`customers/${customerId}/addresses`)
      .then((response) => {
        setAddresses(response.addresses);
        setAddressId(response.addresses[0]?.id || "");
      })
      .catch(() => {
        setAddresses([]);
        setAddressId("");
      });
  }, [customerId]);

  const complete = async () => {
    if (!cart.length) return;
    setBusy(true);
    try {
      const response = await post<{ order: OrderSummary }>("orders", {
        source,
        customerId: customerId || null,
        items: cart.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          selections: item.selections.map((selection) => selection.optionId),
          notes: item.notes,
        })),
        fulfillmentType,
        addressId: fulfillmentType === "delivery" ? addressId : null,
        deliveryZoneId: fulfillmentType === "delivery" ? zoneId : null,
        paymentMethod,
      });
      showToast(`Pedido #${response.order.number} criado e enviado à cozinha.`);
      setCart([]);
      onDone();
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Falha ao criar pedido.", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pos-layout">
      <section className="pos-menu">
        <div className="pos-mode">
          {[
            { id: "counter", label: "Balcão", icon: Store },
            { id: "phone", label: "Telefone", icon: PhoneCall },
            { id: "whatsapp", label: "WhatsApp", icon: MessageCircleMore },
          ].map(({ id, label, icon: Icon }) => (
            <button key={id} className={source === id ? "active" : ""} onClick={() => setSource(id as typeof source)}><Icon size={18} /> {label}</button>
          ))}
        </div>
        <div className="category-tabs pos-categories">
          <button className={categoryId === "all" ? "active" : ""} onClick={() => setCategoryId("all")}>Todos</button>
          {store.categories.map((category) => <button className={categoryId === category.id ? "active" : ""} onClick={() => setCategoryId(category.id)} key={category.id}>{category.name}</button>)}
        </div>
        <div className="pos-products">
          {products.map((item) => (
            <button key={item.id} onClick={() => setProduct(item)}>
              <img src={item.image || "/assets/maxhouse-background.png"} alt="" />
              <span><strong>{item.name}</strong><small>{money(item.basePrice || item.fromPrice || 0)}</small></span>
              <Plus size={18} />
            </button>
          ))}
        </div>
      </section>
      <aside className="pos-cart">
        <header><span><ShoppingBag size={20} /><strong>Novo pedido</strong></span><b>{cart.length}</b></header>
        {source !== "counter" && (
          <label>Cliente
            <select value={customerId} onChange={(event) => setCustomerId(event.target.value)}>
              <option value="">Selecione um cliente</option>
              {customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.name} · {phoneMask(customer.phone)}</option>)}
            </select>
          </label>
        )}
        <div className="pos-cart-items">
          {cart.map((item) => (
            <article key={item.cartId}>
              <span><b>{item.quantity}×</b><span><strong>{item.name}</strong>{item.selections.map((selection) => <small key={selection.optionId}>{selection.optionName}</small>)}</span></span>
              <strong>{money(item.unitPrice * item.quantity)}</strong>
              <button onClick={() => setCart((current) => current.filter((candidate) => candidate.cartId !== item.cartId))}><X size={15} /></button>
            </article>
          ))}
          {!cart.length && <EmptyState icon={ShoppingBag} title="Pedido vazio" text="Toque em um item do cardápio." />}
        </div>
        <div className="pos-controls">
          <div className="segmented compact">
            <button className={fulfillmentType === "counter" ? "active" : ""} onClick={() => setFulfillmentType("counter")}>Consumir/retirar</button>
            <button className={fulfillmentType === "pickup" ? "active" : ""} onClick={() => setFulfillmentType("pickup")}>Retirada</button>
            {source !== "counter" && <button className={fulfillmentType === "delivery" ? "active" : ""} onClick={() => setFulfillmentType("delivery")}>Entrega</button>}
          </div>
          {fulfillmentType === "delivery" && (
            <>
              <label>Endereço
                <select value={addressId} onChange={(event) => setAddressId(event.target.value)}>
                  <option value="">Selecione o endereço</option>
                  {addresses.map((address) => <option key={address.id} value={address.id}>{address.street}, {address.number} · {address.neighborhood}</option>)}
                </select>
              </label>
              <label>Bairro / taxa
                <select value={zoneId} onChange={(event) => setZoneId(event.target.value)}>
                  <option value="">Selecione o bairro</option>
                  {store.deliveryZones.map((zone) => <option key={zone.id} value={zone.id}>{zone.name} · {money(zone.fee)}</option>)}
                </select>
              </label>
            </>
          )}
          <label>Pagamento
            <select value={paymentMethod} onChange={(event) => setPaymentMethod(event.target.value)}>
              <option value="counter">Pagamento no balcão</option>
              <option value="cash">Dinheiro</option>
              <option value="credit">Crédito</option>
              <option value="debit">Débito</option>
              <option value="pix">Pix</option>
            </select>
          </label>
          <div className="pos-total"><span>Total</span><strong>{money(total)}</strong></div>
          <button className="primary-button" disabled={!cart.length || busy || (source !== "counter" && !customerId) || (fulfillmentType === "delivery" && (!addressId || !zoneId))} onClick={complete}>
            {busy ? "Enviando..." : "Finalizar e enviar"} <ArrowRight size={17} />
          </button>
        </div>
      </aside>
      <ProductDialog product={product} onClose={() => setProduct(null)} onAdd={(item) => setCart((current) => [...current, item])} />
    </div>
  );
}

function CourierClose({
  summary,
  couriers,
  orders,
  closings,
  onDone,
  showToast,
}: {
  summary: OperationsSummary | null;
  couriers: Courier[];
  orders: OrderSummary[];
  closings: ClosingState;
  onDone: () => Promise<void> | void;
  showToast: (message: string, tone?: "success" | "error") => void;
}) {
  const [selectedCourier, setSelectedCourier] = useState<Courier | null>(null);
  const [extras, setExtras] = useState("");
  const [advances, setAdvances] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!selectedCourier) return;
    setBusy(true);
    try {
      await post(`closings/couriers/${selectedCourier.id}`, {
        extras: Number(extras.replace(",", ".")) || 0,
        advances: Number(advances.replace(",", ".")) || 0,
        notes,
      });
      showToast(`Turno de ${selectedCourier.name} fechado e registrado.`);
      setSelectedCourier(null);
      setExtras("");
      setAdvances("");
      setNotes("");
      await onDone();
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Falha ao fechar turno.", "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div className="management-layout">
        <section className="admin-card">
          <header className="admin-card-header"><div><span className="eyebrow">Fechamento diário</span><h2>Acerto dos motoboys</h2></div><button className="secondary-button" onClick={() => window.print()}><Printer size={17} /> Imprimir fechamento</button></header>
          <div className="courier-close-list">
            {couriers.filter((courier) => courier.active).map((courier) => {
              const courierSummary = summary?.couriers.find((row) => row.id === courier.id);
              const deliveries = orders.filter((order) => order.courierId === courier.id && order.status === "delivered");
              const closed = closings.courierShifts.find((shift) => shift.courierId === courier.id);
              const deliveryFees = closed?.deliveryFees ?? courierSummary?.deliveryFees ?? 0;
              const total = closed?.amountDue ?? deliveryFees + courier.fixedDailyRate;
              return (
                <article key={courier.id}>
                  <header><span className="user-avatar">{courier.name.split(" ").map((part) => part[0]).slice(0, 2).join("")}</span><span><strong>{courier.name}</strong><small>{courier.vehicle} · {courier.plate}</small></span><span className={`status-pill status-${closed ? "ready" : "delivered"}`}>{closed ? `Fechado ${new Date(closed.closedAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}` : "Em atividade"}</span></header>
                  <div className="courier-close-stats">
                    <span><small>Entregas</small><strong>{closed?.deliveryCount ?? courierSummary?.deliveryCount ?? deliveries.length}</strong></span>
                    <span><small>Taxas recebidas</small><strong>{money(deliveryFees)}</strong></span>
                    <span><small>Diária fixa</small><strong>{money(courier.fixedDailyRate)}</strong></span>
                    <span className="highlight"><small>Total do acerto</small><strong>{money(total)}</strong></span>
                  </div>
                  <footer>
                    <span><PhoneCall size={15} /> {phoneMask(courier.phone)}</span>
                    <button disabled={Boolean(closed)} onClick={() => setSelectedCourier(courier)}>
                      <ClipboardCheck size={17} /> {closed ? "Turno fechado" : "Fechar turno"}
                    </button>
                  </footer>
                </article>
              );
            })}
          </div>
        </section>
      </div>
      <Modal open={Boolean(selectedCourier)} onClose={() => setSelectedCourier(null)} title={`Fechar turno · ${selectedCourier?.name || ""}`} eyebrow="Acerto do motoboy">
        <div className="stack-form">
          <div className="info-box"><ClipboardCheck size={18} /> Entregas e taxas são calculadas automaticamente pelos pedidos entregues hoje.</div>
          <label>Extras<input value={extras} onChange={(event) => setExtras(event.target.value)} inputMode="decimal" placeholder="0,00" /></label>
          <label>Adiantamentos<input value={advances} onChange={(event) => setAdvances(event.target.value)} inputMode="decimal" placeholder="0,00" /></label>
          <label>Observações<textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Ex.: combustível, ajuste combinado..." maxLength={500} /></label>
          <button className="primary-button" disabled={busy} onClick={submit}>{busy ? "Registrando..." : "Confirmar fechamento"}</button>
        </div>
      </Modal>
    </>
  );
}

function FinanceClose({
  summary,
  orders,
  closings,
  onDone,
  showToast,
}: {
  summary: OperationsSummary | null;
  orders: OrderSummary[];
  closings: ClosingState;
  onDone: () => Promise<void> | void;
  showToast: (message: string, tone?: "success" | "error") => void;
}) {
  const completed = orders.filter((order) => order.status === "delivered");
  const cancelled = orders.filter((order) => order.status === "cancelled");
  const expectedCash = summary?.payments.find((payment) => payment.method === "cash")?.total || 0;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [openingAmount, setOpeningAmount] = useState("");
  const [countedAmount, setCountedAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const closeCash = async () => {
    setBusy(true);
    try {
      await post("closings/cash", {
        openingAmount: Number(openingAmount.replace(",", ".")) || 0,
        countedAmount: Number(countedAmount.replace(",", ".")) || 0,
        notes,
      });
      showToast("Caixa do dia fechado e registrado.");
      setDialogOpen(false);
      await onDone();
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Falha ao fechar caixa.", "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div className="stats-grid">
        <StatCard icon={CircleDollarSign} label="Faturamento" value={money(summary?.grossSales || 0)} foot="Vendas não canceladas" tone="red" />
        <StatCard icon={PackageCheck} label="Finalizados" value={String(completed.length)} foot="Pedidos entregues" tone="green" />
        <StatCard icon={Clock3} label="Em aberto" value={String(orders.filter((order) => !["delivered", "cancelled"].includes(order.status)).length)} foot="Ainda na operação" tone="blue" />
        <StatCard icon={CircleAlert} label="Cancelados" value={String(cancelled.length)} foot={money(cancelled.reduce((sum, order) => sum + order.total, 0))} tone="gold" />
      </div>
      <div className="dashboard-grid">
        <section className="admin-card">
          <header className="admin-card-header"><div><span className="eyebrow">Conferência</span><h2>Vendas por pagamento</h2></div><button className="secondary-button" onClick={() => window.print()}><Printer size={17} /> Imprimir</button></header>
          <div className="payment-breakdown">
            {(summary?.payments || []).map((payment) => (
              <div key={payment.method}><span className="payment-icon"><CreditCard size={19} /></span><span><strong>{paymentLabel[payment.method] || payment.method}</strong><small>{payment.count} pedido(s)</small></span><b>{money(payment.total)}</b></div>
            ))}
          </div>
        </section>
        <aside className="admin-card close-summary">
          <header className="admin-card-header"><div><span className="eyebrow">Fechamento do dia</span><h2>Resumo para caixa</h2></div></header>
          <span><small>Vendas brutas</small><strong>{money(summary?.grossSales || 0)}</strong></span>
          <span><small>Taxas de entrega</small><strong>{money(orders.reduce((sum, order) => sum + order.deliveryFee, 0))}</strong></span>
          <span><small>Ticket médio</small><strong>{money(summary?.averageTicket || 0)}</strong></span>
          {closings.cashSession && <span><small>Diferença do caixa</small><strong>{money(closings.cashSession.differenceAmount)}</strong></span>}
          <button className="primary-button" disabled={Boolean(closings.cashSession)} onClick={() => {
            setCountedAmount(expectedCash.toFixed(2).replace(".", ","));
            setDialogOpen(true);
          }}><HandCoins size={18} /> {closings.cashSession ? "Caixa fechado hoje" : "Iniciar conferência de caixa"}</button>
        </aside>
      </div>
      <Modal open={dialogOpen} onClose={() => setDialogOpen(false)} title="Fechamento de caixa" eyebrow="Conferência diária">
        <div className="stack-form">
          <div className="info-box"><CircleDollarSign size={18} /> Dinheiro esperado em vendas: <strong>{money(expectedCash)}</strong>. Cartões e Pix permanecem discriminados no relatório.</div>
          <label>Fundo de caixa inicial<input value={openingAmount} onChange={(event) => setOpeningAmount(event.target.value)} inputMode="decimal" placeholder="0,00" /></label>
          <label>Valor contado em dinheiro<input value={countedAmount} onChange={(event) => setCountedAmount(event.target.value)} inputMode="decimal" placeholder="0,00" /></label>
          <label>Observações<textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Ex.: sangria, diferença conferida..." maxLength={500} /></label>
          <button className="primary-button" disabled={busy} onClick={closeCash}>{busy ? "Fechando..." : "Confirmar e fechar o dia"}</button>
        </div>
      </Modal>
    </>
  );
}

function CustomersPanel({ customers }: { customers: CustomerRow[] }) {
  const [query, setQuery] = useState("");
  const filtered = customers.filter((customer) => `${customer.name} ${customer.phone} ${customer.email || ""}`.toLowerCase().includes(query.toLowerCase()));
  return (
    <section className="admin-card">
      <header className="admin-card-header"><div><span className="eyebrow">CRM da loja</span><h2>Base de clientes</h2></div><span className="count-chip">{customers.length}</span></header>
      <div className="table-toolbar"><div className="admin-search"><Search size={18} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar cliente" /></div><button className="secondary-button"><Plus size={17} /> Novo cliente</button></div>
      <div className="data-table customer-table">
        <div className="data-row header"><span>Cliente</span><span>Contato</span><span>Pedidos</span><span>Valor total</span><span>Cadastro</span></div>
        {filtered.map((customer) => (
          <div className="data-row" key={customer.id}>
            <span><span className="user-avatar">{customer.name.split(" ").map((part) => part[0]).slice(0, 2).join("")}</span><strong>{customer.name}</strong></span>
            <span><strong>{phoneMask(customer.phone)}</strong><small>{customer.email || "Sem e-mail"}</small></span>
            <span>{customer.ordersCount}</span><span>{money(customer.lifetimeValue)}</span><span>{dateTime(customer.createdAt)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function MenuManager({ store, showToast }: { store: StoreConfig; showToast: (message: string, tone?: "success" | "error") => void }) {
  const [categoryId, setCategoryId] = useState(store.categories[0]?.id || "");
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [draft, setDraft] = useState({ name: "", description: "", basePrice: "" });
  const products = store.categories.find((category) => category.id === categoryId)?.products || [];
  const openEdit = (product: Product) => {
    setEditProduct(product);
    setDraft({ name: product.name, description: product.description || "", basePrice: String(product.basePrice) });
  };
  const save = async () => {
    if (!editProduct) return;
    try {
      await patch(`products/${editProduct.id}`, { name: draft.name, description: draft.description, basePrice: Number(draft.basePrice.replace(",", ".")) });
      showToast("Produto atualizado no banco de dados.");
      setEditProduct(null);
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Falha ao atualizar.", "error");
    }
  };
  return (
    <>
      <div className="menu-admin-layout">
        <aside className="admin-card menu-categories">
          <header><span className="eyebrow">Categorias</span><button><Plus size={17} /></button></header>
          {store.categories.map((category) => <button className={category.id === categoryId ? "active" : ""} key={category.id} onClick={() => setCategoryId(category.id)}><span>{category.name}</span><b>{category.products.length}</b></button>)}
        </aside>
        <section className="admin-card">
          <header className="admin-card-header"><div><span className="eyebrow">Catálogo</span><h2>{store.categories.find((category) => category.id === categoryId)?.name}</h2></div><button className="secondary-button"><Plus size={17} /> Novo produto</button></header>
          <div className="menu-admin-grid">
            {products.map((product) => (
              <article key={product.id} onClick={() => openEdit(product)}>
                <img src={product.image || "/assets/maxhouse-background.png"} alt="" />
                <div><strong>{product.name}</strong><small>{product.optionGroups.length} grupo(s) de opção</small><span>{money(product.basePrice || product.fromPrice || 0)}</span></div>
                <button><Settings size={17} /></button>
              </article>
            ))}
          </div>
        </section>
      </div>
      <Modal open={Boolean(editProduct)} onClose={() => setEditProduct(null)} title="Editar produto" eyebrow="Cardápio">
        <div className="stack-form">
          <label>Nome<input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></label>
          <label>Descrição<textarea value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} /></label>
          <label>Preço base<input value={draft.basePrice} onChange={(event) => setDraft({ ...draft, basePrice: event.target.value })} inputMode="decimal" /></label>
          <div className="info-box"><Boxes size={18} /> Sabores e adicionais permanecem vinculados a este produto.</div>
          <button className="primary-button" onClick={save}>Salvar alterações</button>
        </div>
      </Modal>
    </>
  );
}

function WhatsappPanel({ store }: { store: StoreConfig | null }) {
  return (
    <div className="whatsapp-layout">
      <section className="admin-card whatsapp-hero">
        <span className="whatsapp-orbit"><MessageCircleMore size={32} /></span>
        <span className="eyebrow">Coexistência preparada</span>
        <h2>WhatsApp Business no celular e atendimento no sistema.</h2>
        <p>A estrutura já separa conversas, mensagens, cliente e origem do pedido. Para ativar mensagens reais, falta apenas conectar as credenciais oficiais da Meta.</p>
        <div className="whatsapp-ready-list">
          <span><CircleCheck size={18} /> Número atual preservado: {store ? phoneMask(store.whatsapp) : ""}</span>
          <span><CircleCheck size={18} /> Caixa de entrada multiatendente</span>
          <span><CircleCheck size={18} /> Pedido criado com origem WhatsApp</span>
          <span><CircleCheck size={18} /> Handoff entre automação e humano</span>
        </div>
        <button className="primary-button"><ShieldCheck size={18} /> Configurar API oficial</button>
      </section>
      <aside className="admin-card whatsapp-preview">
        <header><span className="user-avatar">MH</span><span><strong>Max House Atendimento</strong><small><i /> Online no celular</small></span></header>
        <div className="chat-preview">
          <span className="message inbound">Boa noite! Quais sabores especiais vocês têm?</span>
          <span className="message outbound">Boa noite! 🍕 Temos várias opções especiais. Posso montar seu pedido por aqui ou enviar o cardápio completo.</span>
          <span className="message inbound">Quero uma grande de 2 sabores.</span>
          <div className="chat-order-card"><UtensilsCrossed size={20} /><span><strong>Novo pedido em montagem</strong><small>Origem: WhatsApp</small></span></div>
        </div>
      </aside>
    </div>
  );
}

function SettingsPanel({ store }: { store: StoreConfig }) {
  return (
    <div className="settings-grid">
      <section className="admin-card settings-card">
        <header><Store size={21} /><span><strong>Dados da loja</strong><small>Informações públicas e operacionais</small></span></header>
        <div className="settings-form-grid">
          <label>Nome<input defaultValue={store.name} /></label>
          <label>WhatsApp<input defaultValue={phoneMask(store.whatsapp)} /></label>
          <label className="span-2">Endereço<input defaultValue={`${store.address} · ${store.reference}`} /></label>
          <label>Tempo de entrega<input defaultValue={`${store.deliveryWait} min`} /></label>
          <label>Tempo de retirada<input defaultValue={`${store.pickupWait} min`} /></label>
        </div>
        <button className="primary-button">Salvar configurações</button>
      </section>
      <section className="admin-card settings-card">
        <header><WalletCards size={21} /><span><strong>Pagamentos</strong><small>Pix e recebimento na entrega</small></span></header>
        <div className="settings-list"><span><strong>Pix</strong><small>Confirmação manual antes da cozinha</small><i className="on" /></span><span><strong>Crédito</strong><small>Maquininha na entrega</small><i className="on" /></span><span><strong>Débito</strong><small>Maquininha na entrega</small><i className="on" /></span><span><strong>Dinheiro</strong><small>Controle de troco</small><i className="on" /></span></div>
      </section>
      <section className="admin-card settings-card">
        <header><ShieldCheck size={21} /><span><strong>Perfis e permissões</strong><small>Acessos separados por função</small></span></header>
        <div className="permission-chips">{["Administrador", "Gerente", "Balcão", "Telefone", "Cozinha", "Expedição", "Financeiro", "WhatsApp", "Motoboy"].map((role) => <span key={role}>{role}</span>)}</div>
      </section>
    </div>
  );
}

function EmptyState({
  icon: Icon,
  title,
  text,
}: {
  icon: typeof CircleCheck;
  title: string;
  text: string;
}) {
  return <div className="admin-empty"><Icon size={34} /><strong>{title}</strong><p>{text}</p></div>;
}
